import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import axios from "axios";
import configLocal from "../config/index"; // adjust path as needed

const CartContext = createContext({
  cartItems: [],
  totalItems: 0,
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  subtotal: 0,
  shippingCost: 0,
  taxAmount: 0,
  total: 0,
  currencySymbol: "$",
});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [total, setTotal] = useState(0);
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const isFirstMount = useRef(true);
  const BASE_URL = configLocal.baseURL;

  /* ===============================
     Load Cart from LocalStorage
  =============================== */

  useEffect(() => {
    const storedCart = localStorage.getItem("cart");
    console.log("Loaded cart from localStorage:", storedCart);
    if (storedCart) {
      try {
        const parsed = JSON.parse(storedCart);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCartItems(parsed);
        }
      } catch {
        localStorage.removeItem("cart");
      }
    }
    isFirstMount.current = false; // ✅ mark first mount done
  }, []);
  // ✅ Fetch cart from backend on mount
  const fetchCart = async () => {
    try {
      const token = localStorage.getItem("token");
      const sessionId = localStorage.getItem("session_id");

      const headers = token
        ? { Authorization: `Bearer ${token}` } // logged-in user
        : sessionId
        ? { "x-session-id": sessionId } // guest user
        : {};

      const res = await axios.get(`${BASE_URL}/api/cart`, {
        headers,
      });

      console.log("Fetched cart:", res.data);

      if (res.data.success) {
        const items = (res.data.items || []).map((item) => ({
          id: item.product_id,
          name: item.name,
          price: item.price,
          image: item.photo,
          quantity: item.quantity,
        }));

        setCartItems(items);
        localStorage.setItem("cart", JSON.stringify(items));
      }
    } catch (err) {
      console.error("Backend failed → fallback to localStorage");

      const storedCart = localStorage.getItem("cart");
      if (storedCart) {
        setCartItems(JSON.parse(storedCart));
      }
    } finally {
      isFirstMount.current = false;
    }
  };
  useEffect(() => {
    fetchCart();
  }, []);

  /* ===============================
     Save Cart to LocalStorage
  =============================== */
  useEffect(() => {
    if (isFirstMount.current) return;

    localStorage.setItem("cart", JSON.stringify(cartItems));

    if (cartItems.length > 0) {
      fetchCartSummary();
    } else {
      setSubtotal(0);
      setShippingCost(0);
      setTaxAmount(0);
      setTotal(0);
    }
  }, [cartItems]);

  /* ===============================
     Backend-synced Cart Summary
  =============================== */
  const fetchCartSummary = async () => {
    try {
      const token = localStorage.getItem("token");
      const sessionId = localStorage.getItem("session_id");

      const headers = token
        ? { Authorization: `Bearer ${token}` } // logged-in user
        : sessionId
        ? { "x-session-id": sessionId } // guest user
        : {};

      const response = await axios.get(`${BASE_URL}/api/cart/summary`, {
        headers,
      });
      console.log("Cart summary response:", response.data);
      const data = response.data;
      setSubtotal(data.subtotal || 0);
      setShippingCost(data.shipping || 0);
      setTaxAmount(data.tax || 0);
      setTotal(data.total || 0);
      setCurrencySymbol(data.currency || "EGP");
    } catch (err) {
      // ✅ guest with empty cart will 500 — just reset silently
      setSubtotal(0);
      setShippingCost(0);
      setTaxAmount(0);
      setTotal(0);
    }
  };

  // ✅ Fix — remove the argument everywhere it's called (it was being ignored anyway)
  const addToCart = async (product, quantity = 1) => {
    // 1. Update local state
    let updatedItems = [];
    const existing = cartItems.find((item) => item.id === product.id);

    if (existing) {
      updatedItems = cartItems.map((item) =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + quantity } // ✅ FIX
          : item
      );
    } else {
      updatedItems = [
        ...cartItems,
        {
          id: product.id,
          name: product.name,
          price: parseFloat(product.selling_price) || 0,
          image: product.photo,
          quantity: quantity, // ✅ FIX
        },
      ];
    }

    setCartItems(updatedItems);

    // 2. Persist to backend
    try {
      const token = localStorage.getItem("token");
      const sessionId = localStorage.getItem("session_id");

      const headers = {
        "Content-Type": "application/json", // ✅ IMPORTANT
        ...(token
          ? { Authorization: `Bearer ${token}` }
          : sessionId
          ? { "x-session-id": sessionId }
          : {}),
      };

      await fetch(`${BASE_URL}/api/cart/add`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          product_id: product.id,
          quantity: quantity, // ✅ FIX
        }),
      });
    } catch (err) {
      console.error("Cart sync failed:", err);
    }

    await fetchCartSummary();
  };

  const updateQuantity = async (id, quantity) => {
    if (quantity < 1) return removeFromCart(id);

    setCartItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity } : item))
    );

    try {
      const token = localStorage.getItem("token");
      const sessionId = localStorage.getItem("session_id");

      const headers = {
        "Content-Type": "application/json",
        ...(token
          ? { Authorization: `Bearer ${token}` }
          : sessionId
          ? { "x-session-id": sessionId }
          : {}),
      };

      await fetch(`${BASE_URL}/api/cart/update`, {
        method: "PUT", // ✅ FIXED
        headers,
        body: JSON.stringify({
          product_id: id,
          quantity,
        }),
      });
    } catch (err) {
      console.error("Update failed:", err);
    }

    fetchCartSummary();
  };

  const removeFromCart = async (id) => {
    const existing = cartItems.find((item) => item.id === id);
    if (!existing) return;

    const token = localStorage.getItem("token");
    const sessionId = localStorage.getItem("session_id");

    const headers = {
      "Content-Type": "application/json",
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : sessionId
        ? { "x-session-id": sessionId }
        : {}),
    };

    try {
      // 🔥 CASE 1: quantity > 1 → decrease
      if (existing.quantity > 1) {
        const newQty = existing.quantity - 1;

        // update UI
        setCartItems((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, quantity: newQty } : item
          )
        );

        // update backend
        await fetch(`${BASE_URL}/api/cart/update`, {
          method: "PUT",
          headers,
          body: JSON.stringify({
            product_id: id,
            quantity: newQty,
          }),
        });
      } else {
        // 🔥 CASE 2: quantity = 1 → delete

        // update UI
        setCartItems((prev) => prev.filter((item) => item.id !== id));

        // delete from backend
        await fetch(`${BASE_URL}/api/cart/${id}`, {
          method: "DELETE",
          headers,
        });
      }

      await fetchCartSummary();
    } catch (err) {
      console.error("Remove failed:", err);
    }
  };

  const clearCart = async () => {
    setCartItems([]);
    setSubtotal(0);
    setShippingCost(0);
    setTaxAmount(0);
    setTotal(0);
    localStorage.removeItem("cart");
  };

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    subtotal,
    shippingCost,
    taxAmount,
    total,
    currencySymbol,
    totalItems,
    refreshCart: fetchCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
